import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios'
import type { ApiResponse, RequestConfig, RequestCanceller } from './types'
import { tokenRefreshManager } from '@/main'
import { clearAllAuthState, isTokenExpired, forceRedirectToLogin, handleTokenRefreshFailure } from '@/utils/authUtils'
import { apiConfigManager } from './config'

// 请求管理器类
class RequestManager {
  private pendingRequests = new Map<string, AbortController>()
  private retryCount = new Map<string, number>()

  // 生成请求唯一标识
  private generateRequestKey(config: AxiosRequestConfig): string {
    const { method, url, params, data } = config
    return `${method?.toUpperCase()}_${url}_${JSON.stringify(params)}_${JSON.stringify(data)}`
  }

  // 添加请求到待处理列表
  addRequest(config: AxiosRequestConfig): AbortController {
    const key = this.generateRequestKey(config)
    const controller = new AbortController()
    
    // 不自动取消重复请求，让所有请求都完成
    this.pendingRequests.set(key, controller)
    return controller
  }

  // 从待处理列表移除请求
  removeRequest(config: AxiosRequestConfig): void {
    const key = this.generateRequestKey(config)
    this.pendingRequests.delete(key)
    this.retryCount.delete(key)
  }

  // 取消所有请求
  cancelAllRequests(): void {
    this.pendingRequests.forEach(controller => controller.abort())
    this.pendingRequests.clear()
    this.retryCount.clear()
  }

  // 取消指定请求
  cancelRequest(config: AxiosRequestConfig): boolean {
    const key = this.generateRequestKey(config)
    const controller = this.pendingRequests.get(key)
    if (controller) {
      controller.abort()
      this.pendingRequests.delete(key)
      this.retryCount.delete(key)
      return true
    }
    return false
  }

  // 获取重试次数
  getRetryCount(config: AxiosRequestConfig): number {
    const key = this.generateRequestKey(config)
    return this.retryCount.get(key) || 0
  }

  // 增加重试次数
  incrementRetryCount(config: AxiosRequestConfig): void {
    const key = this.generateRequestKey(config)
    const count = this.getRetryCount(config) + 1
    this.retryCount.set(key, count)
  }

  // 检查是否应该重试
  shouldRetry(config: AxiosRequestConfig): boolean {
    const configManager = apiConfigManager.getConfig()
    
    // 如果全局禁用重试，直接返回false
    if (!configManager.enableAutoRetry) {
      return false
    }
    
    // 如果请求配置中明确禁用重试，返回false
    if ((config as any).disableRetry === true) {
      return false
    }
    
    const retryCount = this.getRetryCount(config)
    return retryCount < configManager.maxRetries
  }

  // 获取重试延迟时间
  getRetryDelay(config: AxiosRequestConfig): number {
    const configManager = apiConfigManager.getConfig()
    const retryCount = this.getRetryCount(config)
    return configManager.retryDelay * (retryCount + 1)
  }
}

// 创建请求管理器实例
const requestManager = new RequestManager()

// 基础请求类
export class ApiRequest {
  private axiosInstance: AxiosInstance
  private baseURL: string
  private tokenKey: string
  private refreshTokenKey: string
  private temporaryToken: string | null = null
  private temporaryRefreshToken: string | null = null
  // 请求去重缓存：用于去重相同 GET 请求
  private requestCache = new Map<string, Promise<ApiResponse<any>>>()

  constructor(baseURL: string, tokenKey: string = 'token') {
    this.baseURL = baseURL
    this.tokenKey = tokenKey
    this.refreshTokenKey = `${tokenKey}_refresh` // 添加 refreshToken 的 key

    const config = apiConfigManager.getConfig()
    
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  // 设置拦截器
  private setupInterceptors(): void {
    // 请求拦截器
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // 检查token是否存在且有效
        let token = this.getToken()
        
        // 优化：如果正在刷新中，等待刷新完成
        if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
          const newToken = await tokenRefreshManager.getRefreshPromise()
          if (newToken) {
            token = newToken
          }
        } else if (!token || isTokenExpired(token)) {
          // 如果token不存在或已过期，但有refreshToken，尝试刷新
          const refreshToken = this.getRefreshToken()
          if (refreshToken) {
            try {
              const newToken = await this.refreshAccessToken(refreshToken)
              if (newToken) {
                token = newToken
              } else {
                // 刷新失败，清除所有认证状态
                this.clearAllAuthState()
              }
            } catch (error) {
              console.error('❌ Token刷新过程中出错:', error)
              this.clearAllAuthState()
            }
          }
        }
        
        // 添加token到请求头
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // 添加取消控制器
        const controller = requestManager.addRequest(config)
        config.signal = controller.signal

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // 移除请求记录
        requestManager.removeRequest(response.config)
        return response
      },
      async (error) => {
        // 移除请求记录
        if (error.config) {
          requestManager.removeRequest(error.config)
        }

        // 处理401错误 - 尝试自动刷新token
        if (error.response?.status === 401) {
          const refreshToken = this.getRefreshToken()
          if (refreshToken) {
            try {
              // 尝试刷新token
              const refreshResponse = await this.refreshAccessToken(refreshToken)
              if (refreshResponse) {
                // 刷新成功，重试原请求
                const originalRequest = error.config
                originalRequest.headers.Authorization = `Bearer ${refreshResponse}`
                return this.axiosInstance.request(originalRequest)
              } else {
                // 刷新失败时，认证状态已在 performRefreshToken 中清除并跳转到登录页
                return Promise.reject(error)
              }
            } catch (refreshError) {
              console.error('❌ 刷新token失败:', refreshError)
              // 刷新过程中出错，认证状态已在 performRefreshToken 中清除并跳转到登录页
              return Promise.reject(error)
            }
          } else {
            // 没有refreshToken时清除所有认证状态并跳转到登录页
            this.clearAllAuthState()
            setTimeout(() => {
              forceRedirectToLogin(window.location.pathname)
            }, 100)
            return Promise.reject(error)
          }
        }

        // 处理网络错误和超时
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          // 根据配置决定是否进行自动重试
          if (requestManager.shouldRetry(error.config)) {
            requestManager.incrementRetryCount(error.config)
            await this.delay(requestManager.getRetryDelay(error.config))
            return this.axiosInstance.request(error.config)
          } else {
            // 自动重试已禁用，直接抛出错误
            console.warn('请求超时或连接中断，自动重试已禁用:', error.message)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // 延迟函数
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // 创建请求取消器
  createCanceller(): RequestCanceller {
    let cancelled = false
    return {
      cancel: () => {
        cancelled = true
      },
      isCancelled: () => cancelled
    }
  }

  // 生成请求唯一标识（用于去重）
  private generateRequestKey(config: AxiosRequestConfig): string {
    const { method, url, params, data } = config
    // 只对 GET 请求进行去重
    if (method?.toUpperCase() !== 'GET') {
      return ''
    }
    return `${method?.toUpperCase()}_${url}_${JSON.stringify(params)}_${JSON.stringify(data)}`
  }

  // 通用请求方法
  async request<T = any>(
    config: AxiosRequestConfig,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      // 将RequestConfig中的disableRetry属性传递给axios配置
      if (requestConfig?.disableRetry) {
        (config as any).disableRetry = true
      }
      
      // 检查是否已取消
      if (requestConfig?.cancelToken?.signal.aborted) {
        throw new Error('请求已取消')
      }

      // 请求去重：只对 GET 请求进行去重
      const requestKey = this.generateRequestKey(config)
      if (requestKey && this.requestCache.has(requestKey)) {
        // 如果已经有相同的请求在进行中，直接返回该请求的 Promise
        return this.requestCache.get(requestKey)!
      }

      // 创建新请求
      const requestPromise = this.axiosInstance.request(config)
        .then((response: AxiosResponse<ApiResponse<T>>) => {
          // 检查是否已取消
          if (requestConfig?.cancelToken?.signal.aborted) {
            throw new Error('请求已取消')
          }
          
          // 请求完成后从缓存中移除
          if (requestKey) {
            this.requestCache.delete(requestKey)
          }
          
          return response.data
        })
        .catch((error: any) => {
          // 请求失败也从缓存中移除，允许重试
          if (requestKey) {
            this.requestCache.delete(requestKey)
          }
          throw error
        })

      // 将请求 Promise 存入缓存（只对 GET 请求）
      if (requestKey) {
        this.requestCache.set(requestKey, requestPromise)
      }

      return await requestPromise
    } catch (error: any) {
      // 如果是取消请求，直接抛出
      if (axios.isCancel(error) || error.message === '请求已取消') {
        throw error
      }

      // 处理错误响应
      const errorMessage = error.response?.data?.message || error.message || '请求失败'
      
      if (requestConfig?.showError !== false) {
        console.error('API请求错误:', errorMessage)
      }

      throw new Error(errorMessage)
    }
  }

  // GET请求
  async get<T = any>(
    url: string,
    params?: any,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'GET',
        url,
        params,
      },
      requestConfig
    )
  }

  // POST请求
  async post<T = any>(
    url: string,
    data?: any,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'POST',
        url,
        data,
      },
      requestConfig
    )
  }

  // PUT请求
  async put<T = any>(
    url: string,
    data?: any,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'PUT',
        url,
        data,
      },
      requestConfig
    )
  }

  // DELETE请求
  async delete<T = any>(
    url: string,
    requestConfig?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      {
        method: 'DELETE',
        url,
      },
      requestConfig
    )
  }

  // 取消所有请求
  cancelAllRequests(): void {
    requestManager.cancelAllRequests()
  }

  // 取消指定请求
  cancelRequest(config: AxiosRequestConfig): boolean {
    return requestManager.cancelRequest(config)
  }

  // 设置正式token（存储到localStorage）
  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token)
  }

  // 设置正式refreshToken（存储到localStorage）
  setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.refreshTokenKey, refreshToken)
  }

  // 设置临时token（只在内存中）
  setTemporaryToken(token: string): void {
    this.temporaryToken = token
  }

  // 设置临时refreshToken（只在内存中）
  setTemporaryRefreshToken(refreshToken: string): void {
    this.temporaryRefreshToken = refreshToken
  }

  // 获取token（优先返回正式token，其次临时token）
  getToken(): string | null {
    const formalToken = localStorage.getItem(this.tokenKey)
    return formalToken || this.temporaryToken
  }

  // 获取refreshToken（优先返回正式refreshToken，其次临时refreshToken）
  getRefreshToken(): string | null {
    const formalRefreshToken = localStorage.getItem(this.refreshTokenKey)
    return formalRefreshToken || this.temporaryRefreshToken
  }

  // 清除临时token
  clearTemporaryToken(): void {
    this.temporaryToken = null
    this.temporaryRefreshToken = null
  }

  // 清除token
  clearToken(): void {
    // 在开发环境中，跳过清除操作以保持localStorage数据
    if (import.meta.env.DEV) {
      console.log('开发环境：跳过清除token操作，保持localStorage数据')
      return
    }
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem(this.refreshTokenKey) // 同时清除 refreshToken
  }

  // 清除所有认证状态（包括用户信息）
  clearAllAuthState(): void {
    clearAllAuthState('请求拦截器')
  }

  // 刷新访问令牌（带防重复调用保护）
  async refreshAccessToken(refreshToken: string): Promise<string | null> {
    // 如果正在刷新，返回现有的promise
    if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
      return tokenRefreshManager.getRefreshPromise()
    }
    
    // 开始刷新
    tokenRefreshManager.setRefreshing(true)
    const refreshPromise = this.performRefreshToken(refreshToken)
    tokenRefreshManager.setRefreshPromise(refreshPromise)
    
    try {
      const result = await refreshPromise
      return result
    } finally {
      // 刷新完成，重置状态
      tokenRefreshManager.setRefreshing(false)
      tokenRefreshManager.setRefreshPromise(null)
    }
  }

  // 执行实际的刷新操作
  private async performRefreshToken(refreshToken: string): Promise<string | null> {
    try {
      // 获取当前选择的品牌ID
      const currentBrandId = this.getCurrentBrandId()
      
      const response = await this.axiosInstance.post('/api/auth/refresh', {
        refreshToken: refreshToken,
        brandId: currentBrandId // 添加品牌ID
      })
      
      if (response.data.success && response.data.data) {
        const newAccessToken = response.data.data
        this.setToken(newAccessToken)
        return newAccessToken
      } else {
        // 刷新失败时清除所有认证状态并跳转到登录页
        console.warn('❌ Token刷新失败:', response.data.message || '刷新令牌无效或已过期')
        // 延迟跳转，避免在请求拦截器中立即跳转导致问题
        setTimeout(() => {
          handleTokenRefreshFailure('请求拦截器', window.location.pathname)
        }, 100)
        return null
      }
    } catch (error) {
      console.error('❌ 刷新访问令牌失败:', error)
      // 刷新失败时清除所有认证状态并跳转到登录页
      // 延迟跳转，避免在请求拦截器中立即跳转导致问题
      setTimeout(() => {
        handleTokenRefreshFailure('请求拦截器', window.location.pathname)
      }, 100)
      return null
    }
  }

  // 获取当前品牌ID
  private getCurrentBrandId(): number | null {
    try {
      const currentBrand = localStorage.getItem('currentBrand')
      if (currentBrand) {
        const brandData = JSON.parse(currentBrand)
        return brandData.id || null
      }
      return null
    } catch (error) {
      console.warn('获取当前品牌ID失败:', error)
      return null
    }
  }

  // 更新baseURL
  updateBaseURL(baseURL: string): void {
    this.baseURL = baseURL
    this.axiosInstance.defaults.baseURL = baseURL
  }


}

// 创建默认的API请求实例
export const apiRequest = new ApiRequest(
  import.meta.env.VITE_API_BASE_URL || '',
  'apiToken'
)

// 导出请求管理器
export { requestManager } 